/**
 * Lightweight reimplementation of shadcn's `useToast` hook.
 * Maintains a single global toast queue so callers across the tree can fire
 * toasts without prop-drilling.
 *
 * Two exports, and the choice between them is not stylistic:
 * - `notify.success/error/info(message)` — the everyday one-line outcome toast.
 *   Use this. It is a plain object, not a hook, so it works in mutation
 *   callbacks and event handlers without a subscription.
 * - `toast({ title, description, variant, action })` — the raw shadcn call, for
 *   the few toasts that need a title, a JSX body or an action button.
 *
 * There used to also be a `src/hooks/useToast.ts` shim wrapping `notify` in a
 * hook, plus four hand-copied `const notify = {…}` adapters. One store had two
 * public APIs and five spellings; if you're adding a sixth, add it here instead.
 */
import * as React from 'react'
import type { ToastActionElement, ToastProps } from './toast'

const TOAST_LIMIT = 4
const TOAST_REMOVE_DELAY = 4000

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

type State = { toasts: ToasterToast[] }
type Action =
  | { type: 'ADD_TOAST'; toast: ToasterToast }
  | { type: 'UPDATE_TOAST'; toast: Partial<ToasterToast> & { id: string } }
  | { type: 'DISMISS_TOAST'; toastId?: string }
  | { type: 'REMOVE_TOAST'; toastId?: string }

let count = 0
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

const listeners: Array<(state: State) => void> = []
let memoryState: State = { toasts: [] }

const removeQueue = new Map<string, ReturnType<typeof setTimeout>>()
function queueRemoval(toastId: string) {
  if (removeQueue.has(toastId)) return
  const timeout = setTimeout(() => {
    removeQueue.delete(toastId)
    dispatch({ type: 'REMOVE_TOAST', toastId })
  }, TOAST_REMOVE_DELAY)
  removeQueue.set(toastId, timeout)
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD_TOAST':
      return { ...state, toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) }
    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      }
    case 'DISMISS_TOAST': {
      const { toastId } = action
      if (toastId) queueRemoval(toastId)
      else state.toasts.forEach((t) => queueRemoval(t.id))
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined ? { ...t, open: false } : t,
        ),
      }
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) return { ...state, toasts: [] }
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.toastId) }
  }
}

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((l) => l(memoryState))
}

export type ToastInput = Omit<ToasterToast, 'id'>

function toast(props: ToastInput) {
  const id = genId()
  const update = (next: ToastInput) =>
    dispatch({ type: 'UPDATE_TOAST', toast: { ...next, id } })
  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id })
  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })
  return { id, dismiss, update }
}

export function useToast() {
  const [state, setState] = React.useState<State>(memoryState)
  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const idx = listeners.indexOf(setState)
      if (idx > -1) listeners.splice(idx, 1)
    }
  }, [])
  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
  }
}

/**
 * The three-outcome shorthand. `info` is a grey toast on purpose — hue is
 * reserved for human attention, and "saved" is not a decision.
 */
export const notify = {
  success: (message: string) => toast({ description: message, variant: 'success' }),
  error: (message: string) => toast({ description: message, variant: 'destructive' }),
  info: (message: string) => toast({ description: message, variant: 'info' }),
}

export { toast }
